import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Subtitle from 'components/Subtitle';
import Loader from 'components/SectionLoader';
import MemberUnits from 'components/MemberUnits';
import { Box, TextField, Typography } from '@mui/material';
import { fetchUnits } from 'api/portal';
import Permissions from 'components/Permissions';
import { useTranslation } from 'react-i18next';
import Fade from 'components/Fade';

function MemberPermissionsSection({ member, values, errors, onChange }) {
  const { email, phoneNumber, selectedPermissions, selectedUnits } = values;

  const [units, setUnits] = useState([]);
  const [isLoadingUnits, setIsLoadingUnits] = useState(true);
  const [errorUnits, setErrorUnits] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    const searchParams = new URLSearchParams();

    searchParams.set('page_size', 9999);

    setIsLoadingUnits(true);

    fetchUnits(searchParams)
      .then((response) => {
        setIsLoadingUnits(false);

        const { data } = response;

        setUnits(data.results);
        setErrorUnits(false);
      })
      .catch(() => {
        setIsLoadingUnits(false);
        setErrorUnits(true);
      });
  }, []);

  return (
    <>
      <Subtitle>{t('organization.organization-members-edit-permissions.title-sub-member-contact')}</Subtitle>
      <Typography variant="body2" color="secondary" marginBottom="24px">
        {t('organization.organization-members-edit-permissions.text-organization-members-contact')}
      </Typography>

      <Loader loading={!member.id}>
        <Fade>
          <Box maxWidth="428px" marginBottom="48px">
            <TextField
              id="email"
              sx={{ margin: '0 0 16px 0' }}
              value={email}
              label={t('organization.organization-members-edit-permissions.input-email')}
              variant="outlined"
              onChange={(event) => onChange({ email: event.target.value })}
              data-testid="email-text-field"
              inputProps={{ 'data-testid': 'email-input' }}
              FormHelperTextProps={{ 'data-testid': 'email-error' }}
              error={errors.has('email')}
              helperText={errors.get('email')}
              fullWidth
            />
            <TextField
              id="phone"
              sx={{ margin: '16px 0 0' }}
              value={phoneNumber}
              label={t('organization.organization-members-edit-permissions.input-phone')}
              variant="outlined"
              onChange={(event) => onChange({ phoneNumber: event.target.value })}
              data-testid="phone-text-field"
              inputProps={{ 'data-testid': 'phone-input' }}
              FormHelperTextProps={{ 'data-testid': 'phone-error' }}
              error={errors.has('phoneNumber')}
              helperText={errors.get('phoneNumber')}
              fullWidth
            />
          </Box>
        </Fade>
      </Loader>

      <Box marginBottom="48px" data-testid="permission-list">
        <Subtitle>{t('organization.organization-members-edit-permissions.title-sub-member-roles')}</Subtitle>
        <Typography variant="body2" color="secondary" marginBottom="24px">
          {t('organization.organization-members-edit-permissions.text-organization-members-role')}
        </Typography>
        <Loader loading={!member.id}>
          <Box>
            {errors.has('permissions') ? (
              <Typography variant="body2" color="error" data-testid="permission-error">
                {errors.get('permissions')}
              </Typography>
            ) : (
              <Permissions
                selected={selectedPermissions}
                onToggle={(permissions) => onChange({ selectedPermissions: permissions })}
                data-testid="permissions"
              />
            )}
          </Box>
        </Loader>
      </Box>

      <Box marginBottom="48px">
        <Subtitle>{t('organization.organization-members-edit-permissions.title-sub-member-units')}</Subtitle>
        <Typography variant="body2" color="secondary" marginBottom="24px">
          {t('organization.organization-members-edit-permissions.text-organization-members-units')}
        </Typography>
        <Loader loading={!member.id}>
          <MemberUnits
            error={errorUnits}
            isLoading={isLoadingUnits}
            onToggle={(unitIds) => onChange({ selectedUnits: unitIds })}
            selectedUnits={selectedUnits}
            units={units}
            data-testid="units"
          />
        </Loader>
      </Box>
    </>
  );
}

MemberPermissionsSection.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.number,
  }),
  values: PropTypes.shape({
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    selectedPermissions: PropTypes.arrayOf(PropTypes.number),
    selectedUnits: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  errors: PropTypes.instanceOf(Map).isRequired,
  onChange: PropTypes.func.isRequired,
};

MemberPermissionsSection.defaultProps = {
  member: {},
};

export default MemberPermissionsSection;
